import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside, useSetDropdownWidth, calculateDropdownPosition } from 'services/utils/dropdownBehavior'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import '../../../style/scss/forms.scss';

interface DropdownProps<T> {
    name?: string;
    id?: string;
    options: T[];
    selectedOption?: T | null;
    onChange: (selectedValueOrId: string) => void;
    disabled_selected?: string;
    disabled?: boolean;
    showSearch?: boolean;
    errorMessage?: string | string[] | undefined;
    isInvalid?: boolean;
    backendField?: string;
    groupByField?: string;
    sortOptions?: boolean;
    selectionFormat?: (option: T) => string;
    optionFormat?: string | ((option: T) => string);
    optionMode?: 'text' | 'dot' | 'label' | 'dot-text'; // Dot text shows a color dot in front of the text when the option has a color
    translationPrefix?: string;
    allowNoneOption?: boolean;
    noneOptionLabel?: string;
    fixedDropdownWidth?: number;
}

const Dropdown = <T extends { id?: number; [key: string]: any }>({ 
    name, id, options, onChange, disabled_selected, backendField, groupByField, sortOptions = false, selectionFormat = (option) => `${option.id}`, optionFormat = 'id',
    optionMode = 'text', selectedOption, allowNoneOption, noneOptionLabel = 'forms.none_option', errorMessage, showSearch = true, disabled, isInvalid, fixedDropdownWidth, 
    translationPrefix
}: DropdownProps<T>) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownListRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const fieldRef = useRef<HTMLDivElement>(null);
    const [dropdownWidth, setDropdownWidth] = useState<number>(fixedDropdownWidth ?? 200);
    const [dropdownAbove, setDropdownAbove] = useState(false);
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number | undefined>(undefined);

    // Use dropdownBehavior.ts to close the dropdown when clicking outside
    useClickOutside(isOpen, dropdownRef, setIsOpen);

    // Use dropdownBehavior.ts to set the dropdown width on open
    useSetDropdownWidth(fieldRef, setDropdownWidth, fixedDropdownWidth);

    // Autofocus the search field when opening the dropdown list
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                searchRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Get the option format, either from a given function or from a string
    const getOptionFormat = (option: T): string => {
        if (typeof optionFormat === 'function') {
            return optionFormat(option);
        } else if (typeof optionFormat === 'string') {
            const value = option[optionFormat];
            return typeof value === 'string' ? value : '';
        }

        return 'Unknown optionFormat';
    };

    // Add the translation prefix, if given
    const translateOption = (value: string): string => {
        if (translationPrefix) {
            return t(`${translationPrefix}${value}`);
        }

        return t(value);
    };

    // Handle the opening or closing of the dropdown
    const handleToggleDropdown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) return;
        e.stopPropagation();

        // Calculate the dropdown position
        if (!isOpen) {
            calculateDropdownPosition(fieldRef, setDropdownAbove, setDropdownMaxHeight);
        }

        // Open or close the dropdown
        setIsOpen(prev => !prev);
    };

    // Filter out empty values
    const filteredOptions = options.filter(option => {
        if (!optionFormat || (optionFormat && getOptionFormat(option) && getOptionFormat(option).trim() !== "")) {
            return Object.values(option).some(value => String(value).toLowerCase().includes(search.toLowerCase()));
        }
        return false;
    });

    // Optionally sort the options
    const sortedOptions = sortOptions
        ? [...filteredOptions].sort((a, b) => getOptionFormat(a).localeCompare(getOptionFormat(b)))
        : filteredOptions;

    // Optionally group options by field
    const groupedOptions = groupByField
        ? sortedOptions.reduce((result, option) => {
            const group = option[groupByField] ? String(option[groupByField]) : '';
            if (group) {
                if (result[group]) {
                    result[group].push(option);
                } else {
                    result[group] = [option];
                }
            }
            return result;
        }, {} as Record<string, T[]>)
        : {};

    // Get the sorted groups
    const sortedGroups = Object.keys(groupedOptions);

    // Render the option
    const renderOption = (option: T) => {
        const optionKey = String(option.id || option.value);
        const optionValue = String((backendField && option[backendField]) || option.id || option.value);

        const isActive = selectedOption
            ? (
                (backendField && option[backendField] && selectedOption[backendField] === option[backendField]) ||
                (option.id && selectedOption.id === option.id) || 
                (option.value && selectedOption.value === option.value)
            )
            : false;

        if (optionMode === 'dot') {
            return (
                <div key={optionKey} 
                    className={`custom-dropdown-item dot-option ${option.color} ${isActive ? 'active' : ''}`}
                    onClick={() => { 
                        onChange(optionValue); 
                        setIsOpen(false); 
                    }}>
                    {isActive && (
                        <FontAwesomeIcon 
                            icon={faCheck}
                            className='custom-dropdown-selected-icon' />
                    )}
                </div>
            );
        }

        if (optionMode === 'label') {
            return (
                <div key={optionKey} 
                    className={`custom-dropdown-item label-option ${option.color} ${isActive ? 'active' : ''}`}
                    onClick={() => { 
                        onChange(optionValue); 
                        setIsOpen(false); 
                    }}>
                    {getOptionFormat(option) || undefined}
                    {isActive && (
                        <FontAwesomeIcon 
                            icon={faCheck}
                            className='custom-dropdown-selected-icon' />
                    )}
                </div>
            );
        }

        return (
            <div key={optionKey} 
                className={`custom-dropdown-item ${isActive ? 'active' : ''}`}
                onClick={() => { 
                    onChange(optionValue); 
                    setIsOpen(false); 
                }}>
                {optionMode === 'dot-text' && option.color && (
                    <span className={`color-dot ${option.color}`}></span>
                )}
                {t(translateOption(getOptionFormat(option)))}
                {isActive && (
                    <FontAwesomeIcon 
                        icon={faCheck}
                        className='custom-dropdown-selected-icon' />
                )}
            </div>
        );
    };
      
    return (
        <div className='custom-dropdown-container'>
            <div className={`custom-dropdown-field ${errorMessage || isInvalid ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
                 onMouseDown={handleToggleDropdown}
                 ref={fieldRef}>
                    {(optionMode === 'text' || optionMode === 'dot-text') && (
                        selectedOption
                            ? <span>
                                {optionMode === 'dot-text' && selectedOption.color && (
                                    <span className={`color-dot ${selectedOption.color}`}></span>
                                )}
                                {t(translateOption(selectionFormat(selectedOption)) || selectionFormat(selectedOption))}
                              </span>
                            : <span className='custom-dropdown-placeholder'>{t(disabled_selected) || t('forms.general_disabled_selected')}</span>
                    )}

                    {optionMode === 'dot' && (
                        selectedOption
                            ? <span className={`dot-option ${selectedOption.color}`}></span>
                            : <span className='custom-dropdown-placeholder'>{t(disabled_selected) || t('forms.general_disabled_selected')}</span>
                    )}

                    {optionMode === 'label' && (
                        selectedOption
                            ? <span className={`label-option ${selectedOption.color}`}>{selectionFormat(selectedOption)}</span>
                            : <span className='custom-dropdown-placeholder'>{t(disabled_selected) || t('forms.general_disabled_selected')}</span>
                    )}

                    <FontAwesomeIcon 
                        icon={isOpen && !disabled ? faCaretUp : faCaretDown}
                        className='custom-dropdown-caret' 
                    />
            </div>
            {isOpen && !disabled && (
                <div ref={dropdownRef}
                     className={`custom-dropdown ${dropdownAbove ? 'dropdown-above' : ''}`}  
                     style={{width: dropdownWidth, maxHeight: dropdownMaxHeight}}>
                    <div ref={dropdownListRef}
                         className='custom-dropdown-list' 
                         style={{width: dropdownWidth}}>
                        {showSearch && (
                            <input 
                                type="text" 
                                id={id}
                                name={name}
                                value={search} 
                                className='custom-dropdown-search'
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('forms.search_placeholder')}
                                ref={searchRef}
                            />
                        )}
                        {allowNoneOption !== false &&
                            <div className="custom-dropdown-item" onClick={() => { onChange('none'); setIsOpen(false); }}>
                                {t(noneOptionLabel)}
                            </div>
                        }
                        {groupByField ? (
                            sortedGroups.length > 0 ? (
                                sortedGroups.map((group, index) => (
                                    <div key={group}>
                                        <div className={index === 0 ? 'custom-dropdown-group-title first' : 'custom-dropdown-group-title'}>
                                            {group}
                                        </div>
                                        {groupedOptions[group].map(option => renderOption(option))}
                                    </div>
                                ))
                            ) : allowNoneOption === false ? (
                                <div className='custom-dropdown-item-no-options'>
                                    {t('forms.no_options_to_select_label')}
                                </div>
                            ) : null
                        ) : (
                            sortedOptions.length > 0 ? (
                                sortedOptions.map(option => renderOption(option))
                            ) : allowNoneOption === false ? (
                                <div className='custom-dropdown-item-no-options'>
                                    {t('forms.no_options_to_select_label')}
                                </div>
                            ) : null
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dropdown;